import { useEffect } from "react";
import { useSearchParams } from "react-router";
import { ReportHeader } from "@/components/report-header";
import { EmptyState } from "@/components/ui/empty-state";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";
import { ViewSelector } from "@/components/view-selector";
import { SavedViewProvider } from "@/contexts/saved-view-context";
import { useCustomChannelSelection } from "@/hooks/use-custom-channel-selection";
import { usePageTitle } from "@/hooks/use-page-title";
import { CustomChannelsRuleSetSelector } from "../custom-channels-list/custom-channels-ruleset-selector";
import { CustomChannelScorecard } from "./custom-channel-scorecard";
import { CustomChannelSelector } from "./custom-channel-selector";
import { CustomSubchannelSelector } from "./custom-subchannel-selector";

export default function CustomChannelsExplorer() {
  const pageType = "custom-channels::explorer";
  const { selectedChannel, setSelectedSubchannel } =
    useCustomChannelSelection();
  const [searchParams, setSearchParams] = useSearchParams();

  // Handle subchannel URL parameter
  useEffect(() => {
    const subchannelParam = searchParams.get("subchannel");
    if (subchannelParam && selectedChannel) {
      // Set the subchannel from URL parameter
      setSelectedSubchannel(subchannelParam);
      // Remove the parameter from URL to keep it clean
      const newSearchParams = new URLSearchParams(searchParams);
      newSearchParams.delete("subchannel");
      setSearchParams(newSearchParams, { replace: true });
    }
  }, [searchParams, selectedChannel, setSelectedSubchannel, setSearchParams]);

  // Set page title using channel name
  usePageTitle(
    selectedChannel
      ? `${selectedChannel} - Custom Channels`
      : "Custom Report Card",
  );

  return (
    <SavedViewProvider pageType={pageType}>
      <div className="flex flex-col space-y-4">
        <ReportHeader
          title="Custom Report Card"
          headerExtras={<CustomChannelsRuleSetSelector />}
          right={
            selectedChannel ? <ViewSelector pageType={pageType} /> : undefined
          }
        />

        <Tabs defaultValue="scorecard" className="w-full">
          <div className="flex items-center border-b">
            <TabsList className="border-b-0">
              <TabsTrigger value="scorecard" className="relative">
                <div className="flex items-center gap-1.5">Scorecard</div>
              </TabsTrigger>
            </TabsList>
          </div>

          <TabsContent value="scorecard" className="mt-6">
            <div className="flex flex-wrap gap-2 mb-6">
              <CustomChannelSelector />
              <CustomSubchannelSelector />
            </div>
            {!selectedChannel ? (
              <EmptyState
                title="Select a channel"
                description="Select a channel from the dropdown above to view its report card."
              />
            ) : (
              <CustomChannelScorecard />
            )}
          </TabsContent>
        </Tabs>
      </div>
    </SavedViewProvider>
  );
}
